import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, AlertCircle, CheckCircle2, X, Package, UserPlus, ClipboardList } from 'lucide-react';
import { cn } from '../../lib/utils';
import CustomerForm from './CustomerForm';
import ItemForm from './ItemForm';
import ServiceBillForm from './ServiceBillForm';
import BillSummary from './BillSummary';
import AddModal from '../AddModal/AddModal';
import { laptopAPI, inventoryAPI } from '../../services/api';
import SuccessScreen from '../ui/SuccessScreen';
import BeautifiedErrorAlert from '../ui/BeautifiedErrorAlert';

// Local utility for fast inputs that update parent only on blur/enter
// Local utility for fast inputs that update parent with debounce
const FastInput = ({ value, onValueChange, ...props }) => {
    const [localValue, setLocalValue] = React.useState(value);

    React.useEffect(() => {
        setLocalValue(value);
    }, [value]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            if (localValue !== value) {
                onValueChange(localValue);
            }
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
    }, [localValue, value, onValueChange]);

    return (
        <Input
            {...props}
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
        />
    );
};

const CreateBillModal = ({
    open,
    onOpenChange,
    newBill,
    onBillChange,
    onBillTypeChange,
    onAddItem,
    onUpdateItem,
    onRemoveItem,
    totals,
    products,
    onCreate,
    isLoading = false,
    onCustomerSearch, // Added prop
    onAddServiceBill,
    onUpdateServiceBill,
    onRemoveServiceBill,
    loadProducts,
    onClearDraft,
    modalMessage = null, // Prop from parent (BillPage.js)
    modalErrors = {}, // Backend validation errors from parent
    onClearModalErrors = null // Callback to clear backend errors
}) => {
    const [customerSearchMessage, setCustomerSearchMessage] = React.useState(null);
    const [customerSearchType, setCustomerSearchType] = React.useState(null); // 'success' or 'error'

    const [showAddModal, setShowAddModal] = React.useState(false);
    const [addModalType, setAddModalType] = React.useState('product');
    const [addNotification, setAddNotification] = React.useState(null); // { type: 'success'|'error', message }

    const [localErrors, setLocalErrors] = React.useState({});
    const [localSubmitError, setLocalSubmitError] = React.useState(null);
    const [countdown, setCountdown] = React.useState(3);

    // Merge backend errors with local errors for display
    const allErrors = React.useMemo(() => ({
        ...localErrors,
        ...modalErrors
    }), [localErrors, modalErrors]);

    React.useEffect(() => {
        let timer;
        if (modalMessage?.type === 'success' && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [modalMessage?.type, countdown]);

    // Clear errors when modal closes
    React.useEffect(() => {
        if (!open) {
            setLocalErrors({});
            if (onClearModalErrors) {
                onClearModalErrors();
            }
        }
    }, [open, onClearModalErrors]);

    const showNotification = (type, message) => {
        setAddNotification({ type, message });
        setTimeout(() => setAddNotification(null), 5000);
    };

    const handleAddCategory = async (categoryData) => {
        try {
            await inventoryAPI.createCategory(categoryData);
            if (loadProducts) await loadProducts();
            // showNotification handles in AddModal now
        } catch (err) {
            console.error('Failed to save category:', err);
            // Throwing back to AddModal which now handles its own ultimate notifications
            throw err;
        }
    };

    const handleAddProduct = async (productData) => {
        if (!productData) { // Bulk upload reload
            if (loadProducts) await loadProducts();
            showNotification('success', 'Products uploaded and list refreshed!');
            return;
        }
        try {
            await laptopAPI.createProduct(productData);
            if (loadProducts) await loadProducts();
            // showNotification handles in AddModal now
        } catch (err) {
            console.error('Failed to add product:', err);
            // Throwing back to AddModal which now handles its own ultimate notifications
            throw err;
        }
    };

    const handleConfirm = () => {
        setLocalErrors({});
        setLocalSubmitError(null);
        const newLocalErrors = {};

        const isIntegerLike = (n) =>
            Number.isFinite(n) && Math.abs(n - Math.round(n)) < 1e-9;

        const paidAmount = Number(newBill.customerPaidAmount ?? 0) || 0;
        const grandTotal = Number(totals?.grandTotal ?? 0) || 0;
        const computedPendingAmount = grandTotal - paidAmount;

        // Customer Info Validations
        if (!['Bill', 'Invoice'].includes(newBill.billType)) {
            newLocalErrors.billType = 'Transaction Type is required';
        }
        if (!['B2C', 'B2B'].includes(newBill.businessSaleType)) {
            newLocalErrors.businessSaleType = 'Customer Class is required';
        }

        if (!newBill.customerName?.trim()) newLocalErrors.customerName = 'Customer Name is required';
        if (!newBill.customerContactNumber?.trim()) newLocalErrors.customerContactNumber = 'Contact Number is required';
        if (!newBill.billingAddressLine?.trim()) newLocalErrors.billingAddressLine = 'Address is required';
        if (!newBill.billingCity?.trim()) newLocalErrors.billingCity = 'City is required';
        if (!newBill.billingState?.trim()) newLocalErrors.billingState = 'State is required';
        if (!newBill.billingPincode?.trim()) newLocalErrors.billingPincode = 'Pincode is required';

        if (!newBill.orderDate?.trim()) newLocalErrors.orderDate = 'Order Date is required';
        if (newBill.isDealerWarranty && !newBill.orderExpiryDate?.trim()) {
            newLocalErrors.orderExpiryDate = 'Order Expiry Date is required';
        }

        // Payment amount validations (Paid + Pending)
        if (paidAmount < 0) newLocalErrors.customerPaidAmount = 'Paid amount cannot be negative';
        if (!isIntegerLike(paidAmount)) newLocalErrors.customerPaidAmount = 'Paid amount must be an integer';

        if (computedPendingAmount < 0) newLocalErrors.customerPendingBill = 'Pending amount cannot be negative';
        if (!isIntegerLike(computedPendingAmount)) newLocalErrors.customerPendingBill = 'Pending amount must be an integer';

        // Item Validations
        let isMissingSku = false;

        // Invoice Items (Product rows): only validate if user actually provided that row
        const anyItemsProvided = Array.isArray(newBill.items) && newBill.items.some((item) => {
            if (!item) return false;
            return (
                !!item.productId ||
                Number(item.quantitySold) > 0 ||
                Number(item.sellingPrice) > 0 ||
                (item.skuProductCodes || []).length > 0
            );
        });

        if (anyItemsProvided) {
            newBill.items.forEach((item, index) => {
                const isRowProvided = item && (
                    !!item.productId ||
                    Number(item.quantitySold) > 0 ||
                    Number(item.sellingPrice) > 0 ||
                    (item.skuProductCodes || []).length > 0
                );
                if (!isRowProvided) return;

                if (!item.productId) newLocalErrors[`item_${index}_productId`] = 'Required';
                if (!item.quantitySold || Number(item.quantitySold) <= 0) newLocalErrors[`item_${index}_quantitySold`] = 'Qty > 0';
                if (!item.sellingPrice || Number(item.sellingPrice) <= 0) newLocalErrors[`item_${index}_sellingPrice`] = 'Price > 0';

                if (item.productId && Number(item.quantitySold) > 0) {
                    const product = products.find(p => p.productId === item.productId);
                    const isSerializedProduct = product?.productType === 'SERIALIZED';
                    const requiredSkuCount = Number(item.quantitySold) || 0;
                    const selectedSkuCount = (item.skuProductCodes || []).length;

                    if (isSerializedProduct && selectedSkuCount < requiredSkuCount) {
                        isMissingSku = true;
                        newLocalErrors[`item_${index}_skuProductCodes`] = `Requires ${requiredSkuCount} SKUs`;
                    }
                }
            });
        }

        // Service / Labor Charges rows: only validate if user provided that row
        const anyServicesProvided = Array.isArray(newBill.serviceBills) && newBill.serviceBills.some((sb) => {
            if (!sb) return false;
            return (
                !!sb.description?.trim() ||
                Number(sb.quantity) > 0 ||
                Number(sb.serviceCost) > 0
            );
        });

        if (anyServicesProvided) {
            newBill.serviceBills.forEach((sb, index) => {
                const isRowProvided = sb && (
                    !!sb.description?.trim() ||
                    Number(sb.quantity) > 0 ||
                    Number(sb.serviceCost) > 0
                );
                if (!isRowProvided) return;

                if (!sb.description?.trim()) newLocalErrors[`service_${index}_description`] = 'Required';
                if (!sb.quantity || Number(sb.quantity) <= 0) newLocalErrors[`service_${index}_quantity`] = 'Qty > 0';
                if (!sb.serviceCost || Number(sb.serviceCost) <= 0) newLocalErrors[`service_${index}_serviceCost`] = 'Cost > 0';
            });
        }

        if (!newBill.paymentMethod) newLocalErrors.paymentMethod = 'Payment method is required';
        if (newBill.paymentMethod !== 'CASH' && !newBill.transactionReferenceNumber?.trim()) {
            newLocalErrors.transactionReferenceNumber = 'Transaction reference is required for non-cash payments';
        }
        if (!newBill.paymentStatus) newLocalErrors.paymentStatus = 'Payment status is required';

        if (Object.keys(newLocalErrors).length > 0 || isMissingSku) {
            setLocalErrors(newLocalErrors);
            if (isMissingSku) {
                setLocalSubmitError('Please assign tracking IDs (SKUs) to all serialized products before saving.');
            } else {
                setLocalSubmitError('Please check the highlighted fields above; the request cannot proceed.');
            }
            return;
        }

        onCreate();
    };

    if (!open) return null;
    const handleOpenChange = (newOpen) => {
        if (!isLoading) {
            onOpenChange(newOpen);
        }
    };

    const hasItems = Array.isArray(newBill.items) && newBill.items.length > 0 &&
        newBill.items.some(item => item.productId && item.quantitySold > 0 && item.sellingPrice > 0);
    const hasServices = Array.isArray(newBill.serviceBills) && newBill.serviceBills.length > 0 &&
        newBill.serviceBills.some(sb => sb.description && sb.serviceCost > 0);
    const isServiceOnly = hasServices && !hasItems;

    // Derived payment figures for UI + validation
    const paidAmountValue = Number(newBill.customerPaidAmount ?? 0) || 0;
    const computedPendingAmountValue = (Number(totals?.grandTotal ?? 0) || 0) - paidAmountValue;
    const computedTotalItemsCount =
        (Array.isArray(newBill.items)
            ? newBill.items.reduce((sum, item) => {
                const qty = Number(item?.quantitySold) || 0;
                const hasLine = (Number(item?.sellingPrice) || 0) > 0 || !!item?.productId;
                return sum + (hasLine && qty > 0 ? qty : 0);
            }, 0)
            : 0)
        +
        (Array.isArray(newBill.serviceBills)
            ? newBill.serviceBills.reduce((sum, sb) => {
                const qty = Number(sb?.quantity);
                const hasLine = !!sb?.description?.trim() || (Number(sb?.serviceCost) || 0) > 0;
                const normalizedQty = Number.isFinite(qty) && qty > 0 ? qty : 0;
                return sum + (hasLine ? normalizedQty : 0);
            }, 0)
            : 0);

    return (
        <>
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className={cn(
                "overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100",
                modalMessage?.type === 'success' ? "max-w-md max-h-[500px]" : "max-w-[1100px] max-h-[98vh]"
            )}>
              {modalMessage?.type === 'success' ? (
                <SuccessScreen 
                    message={`${newBill.billType} Created!`}
                    subMessage={`Finalizing ${newBill.billType} state...`}
                    countdown={countdown}
                    onAction={() => setCountdown(0)}
                />
              ) : (
                <>
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-2">
                        <div className="flex items-center gap-5">
                            <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                                <div className="relative p-3.5 rounded-2xl bg-orange-100 text-[#ff6f00] shadow-sm transform hover:rotate-3 transition-transform duration-300">
                                    <Package className="w-8 h-8" />
                                </div>
                            </div>
                            <div>
                                <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                                    {isServiceOnly ? 'Service Invoice' : `New ${newBill.billType}`}
                                    <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Draft</span>
                                </DialogTitle>
                                <DialogDescription className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-1 opacity-80">
                                    Strategic transaction portal for the JET INVENTORY system.
                                </DialogDescription>
                            </div>
                        </div>
                        {(() => {
                            const user = JSON.parse(localStorage.getItem('user') || '{}');
                            const userName = (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (user.username || 'System Admin');
                            return (
                                <div className="inline-flex items-center px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 shadow-sm self-start sm:self-center">
                                    <UserPlus className="w-3.5 h-3.5 mr-2" />
                                    Seller: {userName}
                                </div>
                            );
                        })()}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6 scroll-smooth">

                {/* Local or Modal Errors */}
                {(localSubmitError || modalMessage?.type === 'error') && (
                    <div className="mb-6 animate-in slide-in-from-top-4 duration-300">
                        <BeautifiedErrorAlert 
                            error={localSubmitError || modalMessage?.text} 
                            onDismiss={() => {
                                setLocalSubmitError(null);
                            }} 
                        />
                    </div>
                )}
                
                {addNotification && (
                    <div className="mb-8 p-0 animate-in slide-in-from-top-4 duration-500">
                        <div className={cn(
                            "flex items-start gap-4 p-5 rounded-2xl border shadow-lg shadow-black/5",
                            addNotification?.type === 'success' ? "bg-emerald-50 border-emerald-100 text-emerald-900" : "bg-rose-50 border-rose-100 text-rose-900"
                        )}>
                            <div className={cn("p-2 rounded-xl shrink-0 mt-0.5", addNotification?.type === 'success' ? "bg-emerald-100/50" : "bg-rose-100/50")}>
                                {addNotification?.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertCircle className="h-5 w-5 text-rose-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-black uppercase tracking-wider leading-none mb-1.5 pt-1.5">
                                    {addNotification?.type === 'success' ? 'Success' : 'Attention Required'}
                                </p>
                                <p className="text-xs font-bold leading-relaxed opacity-80">{addNotification?.message}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                            <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Transaction Type</Label>
                            <div className={cn(
                                "flex p-1 bg-slate-100/50 rounded-2xl w-full border border-slate-200",
                                allErrors.billType && "border-rose-400 bg-rose-50/30"
                            )}>
                                <button
                                    type="button"
                                    onClick={() => onBillTypeChange('Bill')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.billType === 'Bill'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-indigo-100 dark:shadow-none text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-50 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    📄 Retail Bill
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onBillTypeChange('Invoice')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.billType === 'Invoice'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-indigo-100 dark:shadow-none text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-50 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    💰 Tax Invoice
                                </button>
                            </div>
                            {allErrors.billType && (
                                <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                    {allErrors.billType}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Customer Class</Label>
                            <div className={cn(
                                "flex p-1 bg-slate-100/50 rounded-2xl w-full border border-slate-200",
                                allErrors.businessSaleType && "border-rose-400 bg-rose-50/30"
                            )}>
                                <button
                                    type="button"
                                    onClick={() => onBillChange('businessSaleType', 'B2C')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.businessSaleType === 'B2C'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-indigo-100 dark:shadow-none text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-50 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    👤 Individual
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onBillChange('businessSaleType', 'B2B')}
                                    className={cn(
                                        "flex-1 py-3 px-6 rounded-xl text-sm font-black transition-all duration-300",
                                        newBill.businessSaleType === 'B2B'
                                            ? "bg-white dark:bg-slate-800 shadow-xl shadow-indigo-100 dark:shadow-none text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-50 dark:ring-white/10"
                                            : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/40 dark:hover:bg-slate-800/40"
                                    )}
                                >
                                    🏢 Business
                                </button>
                            </div>
                            {allErrors.businessSaleType && (
                                <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                    {allErrors.businessSaleType}
                                </p>
                            )}
                        </div>
                    </div>


                    {newBill.businessSaleType === 'B2B' && (
                        <div className="space-y-2">
                            <Label htmlFor="purchaseOrderNumber">Purchase Order Number</Label>
                            <Input
                                id="purchaseOrderNumber"
                                placeholder="Enter PO Number"
                                value={newBill.purchaseOrderNumber || ''}
                                onChange={(e) => onBillChange('purchaseOrderNumber', e.target.value)}
                            />
                        </div>
                    )}

                    <Separator />

                    {customerSearchMessage && (
                        <div className={cn(
                            "p-3 rounded-lg border text-sm",
                            customerSearchType === 'success'
                                ? "bg-green-50 border-green-200 text-green-800"
                                : "bg-blue-50 border-blue-200 text-blue-800"
                        )}>
                            {customerSearchMessage}
                        </div>
                    )}

                    <CustomerForm
                        customer={newBill}
                        onChange={onBillChange}
                        onCustomerSearch={onCustomerSearch}
                        errors={allErrors}
                    />

                    <Separator />


                    <div className="p-4 bg-muted/30 rounded-lg border border-muted-foreground/10 space-y-4">
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="isDealerWarranty"
                                checked={newBill.isDealerWarranty || false}
                                onChange={(e) => onBillChange('isDealerWarranty', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <Label htmlFor="isDealerWarranty" className="text-sm font-medium cursor-pointer">
                                Is this product under dealer warranty?
                            </Label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="orderDate">Order Date <span className="text-destructive">*</span></Label>
                                <Input
                                    id="orderDate"
                                    type="date"
                                    value={newBill.orderDate || ''}
                                    onChange={(e) => onBillChange('orderDate', e.target.value)}
                                    className={cn(allErrors.orderDate && "border-rose-400 bg-rose-50/30 animate-shake")}
                                />
                                {allErrors.orderDate && (
                                    <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                        {allErrors.orderDate}
                                    </p>
                                )}
                            </div>
                            {newBill.isDealerWarranty && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <Label htmlFor="orderExpiryDate">
                                        Order Expiry Date <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        id="orderExpiryDate"
                                        type="date"
                                        value={newBill.orderExpiryDate || ''}
                                        onChange={(e) => onBillChange('orderExpiryDate', e.target.value)}
                                        placeholder={!newBill.isDealerWarranty ? "Optional" : ""}
                                        className={cn(allErrors.orderExpiryDate && "border-rose-400 bg-rose-50/30 animate-shake")}
                                    />
                                    {allErrors.orderExpiryDate && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.orderExpiryDate}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {newBill.isDealerWarranty && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="space-y-2">
                                    <Label htmlFor="shipmentDate">Shipment Date</Label>
                                    <Input
                                        id="shipmentDate"
                                        type="date"
                                        value={newBill.shipmentDate || ''}
                                        onChange={(e) => onBillChange('shipmentDate', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="deliveryDate">Delivery Date</Label>
                                    <Input
                                        id="deliveryDate"
                                        type="date"
                                        value={newBill.deliveryDate || ''}
                                        onChange={(e) => onBillChange('deliveryDate', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>


                    <Separator />

                    {/* Payment Method Field */}

                    <div className="space-y-2">
                        <Label htmlFor="paymentMethod">Payment Method <span className="text-destructive">*</span></Label>
                        <Select
                            value={newBill.paymentMethod || ''}
                            onValueChange={value => {
                                onBillChange('paymentMethod', value);
                                if (localErrors.paymentMethod) setLocalErrors(prev => ({...prev, paymentMethod: null}));
                            }}
                            required
                        >
                            <SelectTrigger id="paymentMethod" className={cn("jit-input dark:bg-slate-900 border-slate-200 dark:border-slate-800", allErrors.paymentMethod && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}>
                                <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="CASH">Cash</SelectItem>
                                <SelectItem value="UPI">UPI</SelectItem>
                                <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                                <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                                <SelectItem value="NET_BANKING">Net Banking</SelectItem>
                            </SelectContent>
                        </Select>
                        {allErrors.paymentMethod && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.paymentMethod}</p>}
                    </div>

                    {newBill.paymentMethod && newBill.paymentMethod !== 'CASH' && (
                        <div className="space-y-2">
                            <Label htmlFor="transactionReferenceNumber" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Transaction Number <span className="text-red-500">*</span></Label>
                            <Input
                                id="transactionReferenceNumber"
                                placeholder="Enter transaction number (e.g. UPI ID or Ref No)"
                                value={newBill.transactionReferenceNumber || ''}
                                onChange={(e) => {
                                    onBillChange('transactionReferenceNumber', e.target.value);
                                    if (localErrors.transactionReferenceNumber) setLocalErrors(prev => ({...prev, transactionReferenceNumber: null}));
                                }}
                                className={cn("jit-input", allErrors.transactionReferenceNumber && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                required
                            />
                            {allErrors.transactionReferenceNumber && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">{allErrors.transactionReferenceNumber}</p>}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="paymentStatus">Payment Status <span className="text-destructive">*</span></Label>
                        {/** Only enable if at least one item with quantity > 0 and unit price > 0 */}
                        <Select
                            value={newBill.paymentStatus || ''}
                            onValueChange={value => {
                                onBillChange('paymentStatus', value);
                                if (localErrors.paymentStatus) setLocalErrors(prev => ({...prev, paymentStatus: null}));
                            }}
                            required
                            disabled={false}
                        >
                            <SelectTrigger id="paymentStatus" className={cn("w-full h-12 rounded-xl border-slate-200 dark:border-slate-800 transition-all font-bold dark:bg-slate-900", allErrors.paymentStatus && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}>
                                <SelectValue placeholder="Select payment status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="PAID">Paid</SelectItem>
                                <SelectItem value="FAILED">Failed</SelectItem>
                                <SelectItem value="REFUNDED">Refunded</SelectItem>
                            </SelectContent>
                        </Select>
                        {allErrors.paymentStatus && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{allErrors.paymentStatus}</p>}
                    </div>

                    <Separator />

                    <div className="space-y-4 bg-[#e4e4e4] dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <span className="bg-primary/10 p-1 rounded">📄</span>
                                Physical Bill Details (Optional)
                            </h3>
                            <div className="flex items-center space-x-2 bg-white/50 dark:bg-slate-800/50 px-3 py-1 rounded-full border border-primary/20">
                                <input
                                    type="checkbox"
                                    id="physicalBillAvailableFlg"
                                    checked={newBill.physicalBillAvailableFlg || false}
                                    onChange={(e) => onBillChange('physicalBillAvailableFlg', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <Label htmlFor="physicalBillAvailableFlg" className="text-xs font-bold text-[#ff6f00] cursor-pointer">
                                    Bill Available?
                                </Label>
                            </div>
                        </div>

                        {newBill.physicalBillAvailableFlg && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="space-y-2">
                                    <Label htmlFor="physicalBillNumber">Physical Bill Number</Label>
                                    <Input
                                        id="physicalBillNumber"
                                        placeholder="Enter physical bill number"
                                        value={newBill.physicalBillNumber || ''}
                                        onChange={(e) => onBillChange('physicalBillNumber', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="physicalBillFile">Attach Physical Bill</Label>
                                    <Input
                                        id="physicalBillFile"
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="cursor-pointer bg-white dark:bg-slate-900"
                                        onChange={(e) => onBillChange('physicalBillFile', e.target.files[0])}
                                    />
                                    {newBill.physicalBillFile && (
                                        <p className="text-xs text-green-600 font-medium">
                                            Selected: {newBill.physicalBillFile.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>


                    <ItemForm
                        items={newBill.items}
                        billType={newBill.billType}
                        onAddItem={onAddItem}
                        onUpdateItem={onUpdateItem}
                        onRemoveItem={onRemoveItem}
                        products={products}
                        onAddNewProduct={() => {
                            setAddModalType('product');
                            setShowAddModal(true);
                        }}
                        onAddNewCategory={() => {
                            setAddModalType('category');
                            setShowAddModal(true);
                        }}
                        errors={allErrors}
                    />

                    <Separator className="my-4" />

                    <ServiceBillForm
                        serviceBills={newBill.serviceBills || []}
                        billType={newBill.billType}
                        onAdd={onAddServiceBill}
                        onUpdate={onUpdateServiceBill}
                        onRemove={onRemoveServiceBill}
                        errors={allErrors}
                    />

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-dashed border-muted-foreground/20">
                            <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                                🏷️ Global Adjustments
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label htmlFor="discountPercent" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Discount (%)
                                    </label>
                                    <div className="relative">
                                        <FastInput
                                            id="discountPercent"
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={newBill.discountPercent || ''}
                                            onValueChange={val => onBillChange('discountPercent', Math.max(0, Math.min(100, parseFloat(val) || 0)))}
                                            placeholder="0"
                                            className="pr-8 font-semibold bg-white"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">%</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="shippingFee" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Shipping Fee
                                    </label>
                                    <div className="relative">
                                        <FastInput
                                            id="shippingFee"
                                            type="number"
                                            min={0}
                                            value={newBill.shippingFee || ''}
                                            onValueChange={val => onBillChange('shippingFee', Math.max(0, parseFloat(val) || 0))}
                                            placeholder="0"
                                            className="pl-8 font-semibold bg-white"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">Rs</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-dashed border-muted-foreground/20">
                            <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                                <ClipboardList className="w-4 h-4" /> Special Notes / Internal Remarks
                            </h4>
                            <Textarea
                                placeholder="Add any special instructions or internal notes here..."
                                value={newBill.customerNotes || ''}
                                onChange={(e) => onBillChange('customerNotes', e.target.value)}
                                className="min-h-[80px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                            />
                        </div>

                        {/* Moved Paid/Pending Amount Row */}
                        <div className="col-span-1 md:col-span-2 space-y-4 bg-blue-50/30 p-4 rounded-xl border border-dashed border-blue-200/50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="customerPaidAmount" className="text-xs font-black uppercase tracking-widest text-blue-600/70">Paid Amount (Rs)</Label>
                                    <FastInput
                                        id="customerPaidAmount"
                                        type="number"
                                        placeholder="0.00"
                                        value={paidAmountValue}
                                        onValueChange={(val) => onBillChange('customerPaidAmount', parseFloat(val) || 0)}
                                        className={cn(
                                            "h-12 bg-white font-bold",
                                            allErrors.customerPaidAmount && "border-rose-400 bg-rose-50/30 animate-shake"
                                        )}
                                    />
                                    {allErrors.customerPaidAmount && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.customerPaidAmount}
                                        </p>
                                    )}
                                    <p className="text-[10px] text-blue-500/60 font-medium italic italic">Note: Please add the paid amount in increments.</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="customerPendingBill" className="text-xs font-black uppercase tracking-widest text-blue-600/70">Pending Amount (Rs)</Label>
                                    <FastInput
                                        id="customerPendingBill"
                                        type="number"
                                        placeholder="0.00"
                                        value={Number.isFinite(computedPendingAmountValue) ? computedPendingAmountValue : 0}
                                        onValueChange={() => { }}
                                        readOnly
                                        className={cn(
                                            "h-12 bg-white font-bold",
                                            allErrors.customerPendingBill && "border-rose-400 bg-rose-50/30 animate-shake"
                                        )}
                                    />
                                    {allErrors.customerPendingBill && (
                                        <p className="text-[10px] font-black text-rose-500 ml-1 uppercase tracking-tighter">
                                            {allErrors.customerPendingBill}
                                        </p>
                                    )}
                                    <p className="text-[10px] text-blue-500/60 font-medium italic">Auto-calculated from Paid Amount.</p>
                                    <div className="pt-2">
                                        <BillSummary
                                            totals={totals}
                                            discountPercent={newBill.discountPercent}
                                            shippingFee={newBill.shippingFee}
                                            paidAmount={newBill.customerPaidAmount}
                                            advanceAmount={newBill.advanceAmount}
                                            isAdvanceMerged={newBill.isAdvanceMerged}
                                            totalItemsCount={computedTotalItemsCount}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                {/* End scroll container */}
                </div>

                <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-between items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                    <div className="hidden sm:block">
                        {onClearDraft && (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={onClearDraft}
                                disabled={isLoading}
                                className="text-rose-600 hover:bg-rose-50 rounded-xl font-bold flex items-center gap-2 opacity-70 hover:opacity-100 transition-all"
                            >
                                <X className="w-4 h-4" /> Clear Master Draft
                            </Button>
                        )}
                    </div>
                    
                    <div className="flex gap-4 w-full sm:w-auto">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                            className="flex-1 sm:flex-none h-12 px-8 rounded-xl border-slate-200 font-black text-slate-500 flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className="flex-1 sm:flex-none h-14 px-16 rounded-2xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-2xl shadow-orange-500/20 transition-all duration-300 flex-1 sm:grow-0"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                `Confirm ${newBill.billType}`
                            )}
                        </Button>
                    </div>
                </DialogFooter>
                </>
              )}
            </DialogContent>
        </Dialog>
        {showAddModal && (
            <AddModal
                modalType={addModalType}
                onClose={() => setShowAddModal(false)}
                onAdd={addModalType === 'category' ? handleAddCategory : handleAddProduct}
            />
        )}
        </>
    );
};

export default React.memo(CreateBillModal);